import { useEffect, useState } from "react";
import type { AnswerSource, SearchProviderWarning } from "../types";
import { PROVIDER_LABELS } from "./StagePill";
import "./SourceRail.css";

interface Props {
  sources: AnswerSource[];
  /** Set by the ChatView so clicking a citation chip elsewhere scrolls to
   * and highlights the matching card here. */
  activeN?: number | null;
  /** Search-fail-strict (redesign): a provider that failed/was unconfigured
   * during this turn's search, surfaced visibly rather than silently
   * dropped — never hard-fails the whole answer. */
  providerErrors?: SearchProviderWarning[];
}

function providerLabel(name: string | null | undefined): string {
  if (!name) return "Other sources";
  return PROVIDER_LABELS[name as keyof typeof PROVIDER_LABELS] ?? name;
}

function letterAvatar(domain: string): string {
  return (domain[0] || "?").toUpperCase();
}

function Favicon({ source }: { source: AnswerSource }) {
  const [failed, setFailed] = useState(false);
  // Task 5.11 — provider-supplied favicon wins when present (Tavily's
  // include_favicon); otherwise try the domain's own /favicon.ico; only
  // fall back to a client-side letter avatar (no network request) on error
  // — never a broken-image icon and never a third-party favicon service.
  const src = source.favicon || `https://${source.domain}/favicon.ico`;
  if (failed || !source.domain) {
    return <span className="source-favicon source-favicon-fallback">{letterAvatar(source.domain)}</span>;
  }
  return (
    <img
      className="source-favicon"
      src={src}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Perplexity-style "Sources N ⌄" panel. Pinned to the right of the answer
 * on desktop (via CSS in the parent — this component only owns its own
 * internal collapse state and the sticky/panel chrome), falls back to a
 * full-width collapsible block on tablet/mobile.
 *
 * The `.source-rail` (list) and `.source-card` (entry) class names are
 * kept stable — they're load-bearing for the Playwright citation-scroll
 * and responsive-overflow tests.
 */
function SourceCard({ s, active }: { s: AnswerSource; active: boolean }) {
  const body = (
    <>
      <div className="source-card-header">
        <span className="source-card-n">{s.n}</span>
        {!s.is_provider_answer && <Favicon source={s} />}
        {s.is_provider_answer && <span className="tag tag-muted source-card-summary-badge">Summary</span>}
        <span className="source-card-domain">{s.domain}</span>
      </div>
      <div className="source-card-title">{s.title}</div>
      {s.snippet && <div className="source-card-snippet">{s.snippet}</div>}
    </>
  );
  // A provider-answer source has no url (it's a synthesized summary, not a
  // fetched page) — render as a non-navigating card rather than a broken
  // empty-href link.
  if (!s.url) {
    return (
      <div id={`source-${s.n}`} className={"source-card" + (active ? " source-card-active" : "")}>
        {body}
      </div>
    );
  }
  return (
    <a
      id={`source-${s.n}`}
      className={"source-card" + (active ? " source-card-active" : "")}
      href={s.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      {body}
    </a>
  );
}

export default function SourceRail({ sources, activeN, providerErrors }: Props) {
  const [collapsed, setCollapsed] = useState(false);

  // A citation chip click elsewhere sets `activeN` — if the panel is
  // currently collapsed, auto-expand so the highlighted card is actually
  // visible (and scrollIntoView-able) rather than hidden in a closed panel.
  useEffect(() => {
    if (activeN != null) setCollapsed(false);
  }, [activeN]);

  if (!sources.length) return null;

  // Group by provider in first-seen order (matches the order search
  // actually ran in, per-mode) — sources with no provider (legacy/persisted
  // rows predating attribution) fall into a trailing "Other sources" group.
  const order: (string | null)[] = [];
  const groups = new Map<string | null, AnswerSource[]>();
  for (const s of sources) {
    const key = s.provider ?? null;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(s);
  }

  return (
    <div className="source-panel">
      <button
        type="button"
        className="source-panel-header"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <span className="source-panel-title">Sources</span>
        <span className="source-panel-count">{sources.length}</span>
        <span className={"source-panel-chevron" + (collapsed ? " source-panel-chevron-collapsed" : "")}>⌄</span>
      </button>
      <div className={"source-rail" + (collapsed ? " source-rail-collapsed" : "")}>
        {providerErrors && providerErrors.length > 0 && (
          <div className="source-rail-warning">
            {providerErrors.map((w, i) => (
              <div key={i}>
                {providerLabel(w.provider)} search failed — results may be incomplete
                {w.message ? ` (${w.message})` : ""}.
              </div>
            ))}
          </div>
        )}
        {order.map((key) => (
          <div key={key ?? "__other__"} className="source-rail-group">
            <div className="source-rail-group-label">{providerLabel(key)}</div>
            {groups.get(key)!.map((s) => (
              <SourceCard key={s.n} s={s} active={activeN === s.n} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
